// "mailto:", "tel:", "https:" … — anything with a URI scheme is not a site path.
const HAS_SCHEME = /^[a-z][a-z0-9+.-]*:/i;

/**
 * True for a site path anchored at the root ("/x") or a same-page "#…"/"?…".
 * "//host" is protocol-relative, i.e. another site, so it does not count.
 */
export const isRootRelative = (href: string): boolean => /^(\/(?!\/)|[#?])/.test(href);

/**
 * Turn an editor-entered link into something safe to hand to next/link.
 *
 * next/link resolves a path without a leading "/" against the current page, so
 * "case-studies/k-health" written on /case-studies/x rendered as
 * /case-studies/case-studies/k-health and 404'd (FOX2-209). Editors mean a site
 * path, so anchor it at the root. Callers such as Cta rely on the result starting
 * with "/" to tell internal links from external ones.
 *
 * - Scheme URLs (https:, mailto:, tel:) pass through unchanged.
 * - "\/x" (an escaped slash some Studio values were saved with) and "//x" collapse to "/x".
 * - Leading "./" and "../" are dropped rather than resolved; body copy and nav
 *   have no meaningful parent page. "./#x" stays a same-page "#x".
 */
const prepInternalUrl = (href?: string | null): string => {
  const trimmed = href?.trim();
  if (!trimmed) return '';
  if (HAS_SCHEME.test(trimmed)) return trimmed;

  const path = trimmed.replace(/^\\\//, '/').replace(/^\/{2,}/, '/');
  if (isRootRelative(path)) return path;

  const rest = path.replace(/^(\.{1,2}(\/|$))+/, '');
  return /^[#?]/.test(rest) ? rest : `/${rest}`;
};

export default prepInternalUrl;
